// Every query and mutation of a workspace's documents (where they are). Keys
// are written here only, under ["ws", slug, "documents", ...]. A document
// itself (its body, title, archive, versions) is read and written through
// ui-core's Document part (/api/me/pages/:id), which keeps its own keys.
//
// Promise:
// - The events of documents are Me's (they do not say the workspace): they
//   read every workspace's documents again (`invalidateWsDocuments`).
// - Moving a document to another space shows at once and rolls back when the
//   server refuses. What is created carries an Idempotency-Key.
import {
  infiniteQueryOptions,
  keepPreviousData,
  type QueryClient,
  type QueryKey,
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  WsPageList,
  WsPagePlace,
} from "@trameria/cloud-api/workspace-documents-shared";
import {
  type Api,
  idempotencyKeyFor,
  type LinkCandidate,
  optimisticMutation,
  type PageDTO,
  titlesFirst,
  useApi,
} from "@trameria/ui-core";

const enc = encodeURIComponent;
const base = (slug: string) => `/api/w/${enc(slug)}`;

export const wsDocumentsPrefix = (slug: string) =>
  ["ws", slug, "documents"] as const;

/** Every workspace's documents are read again (an event names no workspace). */
export const invalidateWsDocuments = (qc: QueryClient) =>
  qc.invalidateQueries({
    predicate: (q) => q.queryKey[0] === "ws" && q.queryKey[2] === "documents",
  });

/** A key of a workspace's list of documents (what is kept across reloads). */
export const isWsListKey = (key: QueryKey) =>
  key[0] === "ws" && key[2] === "documents" && key[3] === "list";

/** Every workspace's lists are read again (a document's row changed). */
export const invalidateWsLists = (qc: QueryClient) =>
  qc.invalidateQueries({ predicate: (q) => isWsListKey(q.queryKey) });

// ---- the list -------------------------------------------------------------------

export interface WsListOptions {
  archived: boolean;
  /** One space only (null: every space one sees). */
  spaceId: string | null;
  /** Words of the title (sent to the server: what is not read yet is found). */
  q?: string;
}

export const wsListPrefix = (slug: string) =>
  [...wsDocumentsPrefix(slug), "list"] as const;

/** The workspace's documents one sees, the latest change first, a page at a time. */
export const wsListQuery = (api: Api, slug: string, options: WsListOptions) =>
  infiniteQueryOptions({
    queryKey: [
      ...wsListPrefix(slug),
      options.archived ? "archived" : "active",
      options.spaceId ?? "all",
      options.q ?? "",
    ] as const,
    queryFn: ({ pageParam }) => {
      const q = new URLSearchParams();
      if (options.archived) q.set("archived", "1");
      if (options.spaceId) q.set("spaceId", options.spaceId);
      if (options.q) q.set("q", options.q);
      if (pageParam) q.set("before", pageParam);
      const qs = q.toString();
      return api<WsPageList>(`${base(slug)}/pages${qs ? `?${qs}` : ""}`);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: keepPreviousData,
  });

/**
 * What an index of the workspace may be given: its documents (not indexes,
 * not one's own), by title or text, titles first.
 */
export const wsCandidatesQuery = (api: Api, slug: string, query: string) =>
  queryOptions({
    queryKey: [...wsDocumentsPrefix(slug), "candidates", query] as const,
    queryFn: async (): Promise<LinkCandidate[]> => {
      const q = new URLSearchParams({ kind: "document", limit: "20" });
      if (query.trim()) {
        q.set("q", query.trim());
        q.set("body", "1");
      }
      const list = await api<WsPageList>(`${base(slug)}/pages?${q}`);
      return titlesFirst(
        list.pages.map((p) => ({ id: p.id, title: p.title })),
        query,
      );
    },
    staleTime: 30_000,
  });

// ---- where one is -----------------------------------------------------------------

export const wsPlaceKey = (slug: string, pageId: string) =>
  [...wsDocumentsPrefix(slug), "place", pageId] as const;

/** The space a workspace's document is in. */
export const wsPlaceQuery = (api: Api, slug: string, pageId: string) =>
  queryOptions({
    queryKey: wsPlaceKey(slug, pageId),
    queryFn: () => api<WsPagePlace>(`${base(slug)}/pages/${enc(pageId)}/place`),
  });

// ---- writes -----------------------------------------------------------------------

/** Makes a document in a space of the workspace. */
export function useCreateWsDocument(slug: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { spaceId: string; title: string }) => ({
      page: (
        await api<{ page: PageDTO }>(`${base(slug)}/pages`, {
          method: "POST",
          body: input,
          idempotencyKey: idempotencyKeyFor(input),
        })
      ).page,
    }),
    onSettled: () =>
      void qc.invalidateQueries({ queryKey: wsListPrefix(slug) }),
  });
}

/** Makes an index in a space of the workspace. */
export function useCreateWsIndex(slug: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { spaceId: string; title: string }) =>
      (
        await api<{ page: PageDTO }>(`${base(slug)}/doc-indexes`, {
          method: "POST",
          body: input,
          idempotencyKey: idempotencyKeyFor(input),
        })
      ).page,
    onSettled: () =>
      void qc.invalidateQueries({ queryKey: wsListPrefix(slug) }),
  });
}

/** Moves a document to another space of the workspace. */
export function useMoveWsDocument(slug: string, pageId: string) {
  const api = useApi();
  const qc = useQueryClient();
  const optimistic = optimisticMutation<
    WsPagePlace,
    { space: WsPagePlace["space"] }
  >(qc, wsPlaceKey(slug, pageId), (old, { space }) =>
    old ? { ...old, space } : old,
  );
  return useMutation({
    mutationFn: (input: { space: WsPagePlace["space"] }) =>
      api<WsPagePlace>(`${base(slug)}/pages/${enc(pageId)}/place`, {
        method: "PUT",
        body: { spaceId: input.space.id },
      }),
    ...optimistic,
    onSettled: () => {
      void optimistic.onSettled();
      void qc.invalidateQueries({ queryKey: wsListPrefix(slug) });
    },
  });
}

/** Moves one's own document into a space of a workspace (one way). */
export function useReleaseDocument() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      pageId: string;
      workspace: string;
      spaceId: string;
    }) =>
      api<WsPagePlace>(`/api/me/pages/${enc(input.pageId)}/release`, {
        method: "POST",
        body: { workspace: input.workspace, spaceId: input.spaceId },
      }),
    onSuccess: (place) =>
      void qc.invalidateQueries({
        queryKey: wsListPrefix(place.workspace.slug),
      }),
  });
}
